import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { CatalogApi } from '@backstage/catalog-client';
import {
  ErrorApi,
  errorApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  RULE_SEVERITIES,
  RuleEntity,
} from '@internal/backstage-plugin-catalog-devops-common';

interface RulesContextProviderProps {
  children: ReactNode;
  accountName: string;
}

enum ActionTypes {
  SELECT = 'SELECT',
  UNSELECT = 'UNSELECT',
  LOAD_COMPLETED = 'LOAD_COMPLETED',
  LOAD_STARTED = 'LOAD_STARTED',
}

type SelectRuleAction = {
  type: ActionTypes.SELECT;
  payload: RuleEntity;
};

type UnselectRuleAction = {
  type: ActionTypes.UNSELECT;
};

type LoadCompletedAction = {
  type: ActionTypes.LOAD_COMPLETED;
  payload: RuleEntity[];
};

type LoadStartedAction = {
  type: ActionTypes.LOAD_STARTED;
};

type RulesAction =
  | SelectRuleAction
  | UnselectRuleAction
  | LoadCompletedAction
  | LoadStartedAction;

type RulesState = {
  rules: RuleEntity[];
  selectedRule: RuleEntity | undefined;
  severities: string[];
  results: string[];
  isLoading: boolean;
};

type RulesContextType = {
  rules: RuleEntity[];
  financeRules: RuleEntity[];
  securityRules: RuleEntity[];
  housekeepingRules: RuleEntity[];
  selectedRule: RuleEntity | undefined;
  severities: string[];
  results: string[];
  isLoading: boolean;
  selectRule(rule: RuleEntity): void;
  unselectRule(): void;
};

const initialRulesState: RulesState = {
  rules: [],
  selectedRule: undefined,
  severities: [],
  results: [],
  isLoading: true,
};

export const RulesContext = createContext<RulesContextType | null>(null);

export function RulesContextProvider(
  props: Readonly<RulesContextProviderProps>,
) {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const errorApi = useApi<ErrorApi>(errorApiRef);

  const rulesReducer = (state: RulesState, action: RulesAction): RulesState => {
    switch (action.type) {
      case ActionTypes.SELECT: {
        const stateCopy = { ...state };
        stateCopy.selectedRule = action.payload;
        return stateCopy;
      }
      case ActionTypes.UNSELECT: {
        const stateCopy = { ...state };
        stateCopy.selectedRule = undefined;
        return stateCopy;
      }
      case ActionTypes.LOAD_STARTED: {
        return { ...state, isLoading: true };
      }
      case ActionTypes.LOAD_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.isLoading = false;
        stateCopy.rules = action.payload;
        const severitiesOrder: string[] = Object.values(RULE_SEVERITIES);
        stateCopy.severities = stateCopy.rules
          .flatMap(rule => rule.spec.resources.map(resource => resource.severity))
          .filter((value, index, current) => current.indexOf(value) === index)
          .sort(
            (a, b) => severitiesOrder.indexOf(a) - severitiesOrder.indexOf(b),
          );
        stateCopy.results = stateCopy.rules
          .map(resource => resource.spec.result)
          .filter(
            (value, index, current_value) =>
              current_value.indexOf(value) === index,
          );
        return stateCopy;
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(rulesReducer, initialRulesState);

  useEffect(() => {
    let cancelled = false;
    dispatch({ type: ActionTypes.LOAD_STARTED });
    const fetchEntities = async () => {
      const filter = {
        kind: 'Rule',
        'spec.account': props.accountName,
      };

      try {
        const filteredRules = await catalogApi.getEntities({ filter });

        if (!cancelled) {
          dispatch({
            type: ActionTypes.LOAD_COMPLETED,
            payload: filteredRules.items as RuleEntity[],
          });
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        // A rejected catalog request must still leave the terminal state,
        // otherwise the rules tab spins forever with no error message.
        errorApi.post(error as Error);
        dispatch({ type: ActionTypes.LOAD_COMPLETED, payload: [] });
      }
    };

    fetchEntities();
    return () => {
      cancelled = true;
    };
  }, [catalogApi, errorApi, props.accountName]);

  const selectRule = (rule: RuleEntity) =>
    dispatch({ type: ActionTypes.SELECT, payload: rule });
  const unselectRule = () => dispatch({ type: ActionTypes.UNSELECT });

  const context = useMemo(
    () => ({
      rules: state.rules,
      financeRules: state.rules.filter(
        rule => rule.spec.category === 'Finance',
      ),
      securityRules: state.rules.filter(
        rule => rule.spec.category === 'Security',
      ),
      housekeepingRules: state.rules.filter(
        rule => rule.spec.category === 'Housekeeping',
      ),
      selectedRule: state.selectedRule,
      severities: state.severities,
      results: state.results,
      isLoading: state.isLoading,
      selectRule,
      unselectRule,
    }),
    [
      state.rules,
      state.selectedRule,
      state.severities,
      state.results,
      state.isLoading,
    ],
  );

  return (
    <RulesContext.Provider value={context}>
      {props.children}
    </RulesContext.Provider>
  );
}
